<template lang="pug">
.full-width-wrapper
  .dashboard-faqs-wrapper
    .dashboard-faqs.dashboard-spacing
      LoadingSpinner(v-if='loading')
      template(v-else)
        h1.header {{ $t('homepage.faqs.faqsAboutAcademy', { academyName: academyName }) }}
        .faq-list
          .faq-item(v-for='faq in visibleFaqs', :key='faq.position')
            .faq-question(@click='toggleFaq(faq.position)')
              h5(:class='{ active: activeFaq === faq.position }', v-html='faq.question')
              .svg-wrapper(:class='{ open: activeFaq === faq.position }')
                SVGRenderer(
                  :icon='arrowDown',
                  width='15px',
                  height='15px',
                  :stroke-color='"var(--primary-text-color)"',
                  :has-hover='false'
                )
            transition(name='onlyFadeIn') 
              .faq-answer(v-if='activeFaq === faq.position')
                h5(v-html='formattedFaqAnswer(faq.answer)')
        .faq-toggle-btn(v-if='filteredFaqs.length > 3', @click='toggleShowAll')
          h5 {{ showAll ? $t('homepage.faqs.showLess') : $t('homepage.faqs.showAll', { faqsLength: filteredFaqs.length }) }}
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted } from 'vue'

  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'

  import useIcons from '@/composables/useIcons'
  import useCourse from '@/composables/useCourse'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useShopifyClient from '@/composables/useShopifyClient'

  import { UserModule } from '@/store/modules/user'

  import * as Sentry from '@sentry/vue'

  declare global {
    interface Window {
      createShopifyCheckout?: () => void
    }
  }

  const props = defineProps<{ type: 'default' | 'purchased' }>()

  const { arrowDown } = useIcons()
  const { course, courseBundle } = useCourse()
  const { extractCourseVertical } = useCommonMixin()
  const { createShopifyCheckout } = useShopifyClient()

  const loading = ref(true)
  const showAll = ref(false)
  const activeFaq = ref<number>(0)

  const academyName = computed(() => {
    return UserModule.currentCompany?.name || 'Sachkundegurus'
  })

  const faqs = computed(() => UserModule.faqs)

  const visibleFaqs = computed(() => {
    if (showAll.value) {
      return filteredFaqs.value
    }
    return filteredFaqs.value.slice(0, 3)
  })

  const formattedFaqAnswer = computed(() => (answer: string) => {
    return answer.replace(
      'Kurs auswählen',
      `<span class="ketch-font-bold ketch-cursor-pointer hover:ketch-underline" onclick="window.createShopifyCheckout()">Kurs auswählen</span>`,
    )
  })

  // Expose createShopifyCheckout to window for onclick handler
  onMounted(() => {
    window.createShopifyCheckout = createShopifyCheckout
  })

  onUnmounted(() => {
    delete window.createShopifyCheckout
  })

  const filteredFaqs = computed(() => {
    let filtered = faqs.value

    if (courseBundle.value) {
      const courseVertical = extractCourseVertical(courseBundle.value?.title || '') || 'General'
      filtered = filtered.filter((faq) => faq.courseVertical === 'General' || faq.courseVertical === courseVertical)
    }

    if (props.type === 'default' && (course?.value?.trial || course?.value?.expired)) {
      filtered = filtered.filter((faq) => faq.visibleInTrialMode)
    }

    return filtered
  })

  const toggleFaq = (position: number) => {
    if (activeFaq.value == position) {
      activeFaq.value = 0
    } else {
      activeFaq.value = position
    }
  }

  const toggleShowAll = () => {
    showAll.value = !showAll.value
  }

  onMounted(async () => {
    try {
      await UserModule.getFaqs(props.type)
    } catch (err) {
      Sentry.captureException(err)
    } finally {
      loading.value = false
    }
  })
</script>

<style lang="postcss" scoped>
  .dashboard-faqs-wrapper {
    @apply ketch-flex ketch-justify-center ketch-bg-content-background-color ketch-mx-c10 ketch-rounded-normal ketch-shadow-content-shadow;
    @apply sm:ketch-mx-0 sm:ketch-rounded-none sm:ketch-shadow-none;

    .dashboard-faqs {
      @apply ketch-w-full ketch-p-c15 sm:ketch-py-c80 md:ketch-px-0;

      .loading {
        @apply ketch-mx-auto;
      }

      .header {
        @apply ketch-font-bold ketch-mb-c20 sm:ketch-mb-c40;
      }

      .faq-list {
        @apply ketch-flex ketch-flex-col ketch-mb-c10 sm:ketch-mb-c20;

        .faq-item {
          @apply ketch-py-c15 ketch-transition-all ketch-border-b ketch-border-border-color last:ketch-border-b-0;
          transition: all 0.3s ease;
          opacity: 1;
          max-height: 1000px;
          overflow: hidden;

          &.hidden {
            opacity: 0;
            max-height: 0;
            padding: 0;
          }

          .faq-question {
            @apply ketch-flex ketch-justify-between ketch-items-center ketch-cursor-pointer;

            h5 {
              &.active {
                @apply ketch-font-bold;
              }
            }

            .svg-wrapper {
              &.open {
                transform: rotate(180deg);
              }
            }
          }

          .faq-answer {
            @apply ketch-mt-c12 ketch-text-gray-700;

            span {
              @apply ketch-block;
            }
          }
        }
      }

      .faq-toggle-btn {
        @apply ketch-w-full ketch-p-c10 ketch-rounded-lg ketch-cursor-pointer;
        @apply ketch-text-center ketch-border ketch-border-border-color ketch-shadow-content-shadow;
      }
    }
  }
</style>
